import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard, FileText, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/shared/SearchInput';
import { Pagination } from '@/components/shared/Pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetTopupOrders } from '@/services/api/endpoints/TopupOrderService';
import { useGetCompanies } from '@/services/api/endpoints/CompanyService';
import { useDebounce } from '@/hooks/useDebounce';
import { formatDate, formatAmount } from '@/lib/utils';
import type { TopupOrderListItem, TopupOrderStatus, FiscalReceiptStatus } from '@/types';

interface OrdersPageProps {
  /** When provided, the list is scoped to a single company and the company
   *  column + filter are hidden (used by the Top-ups tab on Company detail). */
  fixedCompanyId?: string;
  /** Skip the page header when mounted as a tab. */
  embedded?: boolean;
}

export function OrdersPage({ fixedCompanyId, embedded }: OrdersPageProps = {}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [companyId, setCompanyId] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const debounced = useDebounce(search);

  const { data: companies } = useGetCompanies(1, 200);
  const { data, isLoading } = useGetTopupOrders(
    page,
    20,
    debounced || undefined,
    status || undefined,
    fixedCompanyId || companyId || undefined,
    dateFrom || undefined,
    dateTo || undefined,
  );

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">{t('topupOrders.title')}</h1>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <div className="max-w-sm flex-1">
          <SearchInput
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
          />
        </div>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">{t('topupOrders.allStatuses')}</option>
          <option value="completed">{t('topupOrders.statusCompleted')}</option>
          <option value="payment_succeeded">{t('topupOrders.statusPaid')}</option>
          <option value="processing_payment">{t('topupOrders.statusProcessing')}</option>
          <option value="failed">{t('topupOrders.statusFailed')}</option>
          <option value="cancelled">{t('topupOrders.statusCancelled')}</option>
        </select>
        {!fixedCompanyId && (
          <select
            value={companyId}
            onChange={(e) => {
              setCompanyId(e.target.value);
              setPage(1);
            }}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">{t('topupOrders.allCompanies')}</option>
            {companies?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )}
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setPage(1);
          }}
          className="w-40"
        />
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setPage(1);
          }}
          className="w-40"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                        {t('topupOrders.date')}
                      </th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                        {t('topupOrders.user')}
                      </th>
                      {!fixedCompanyId && (
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                          {t('topupOrders.company')}
                        </th>
                      )}
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">
                        {t('topupOrders.amount')}
                      </th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                        {t('topupOrders.method')}
                      </th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">
                        {t('common.status')}
                      </th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">
                        {t('topupOrders.fiscal')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items.map((order) => (
                      <OrderRow
                        key={order.id}
                        order={order}
                        hideCompany={!!fixedCompanyId}
                      />
                    ))}
                    {data?.items.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-muted-foreground">
                          {t('common.noResults')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="md:hidden divide-y">
                {data?.items.map((order) => (
                  <Link
                    key={order.id}
                    to={`/orders/${order.id}`}
                    className="block p-4 space-y-2 hover:bg-muted/30"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {formatDate(order.createdAt)}
                      </span>
                      <TopupStatusBadge status={order.status} />
                    </div>
                    <div className="text-sm">
                      {customerLabel(order)} · {order.company?.name || '—'}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">
                        {formatAmount(order.amount)} {order.currency}
                      </span>
                      <FiscalIndicator fiscal={order.fiscalReceipt} compact />
                    </div>
                  </Link>
                ))}
                {data?.items.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    {t('common.noResults')}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {data?.pagination && (
        <Pagination page={page} totalPages={data.pagination.totalPages} onPageChange={setPage} />
      )}
    </div>
  );
}

function OrderRow({
  order,
  hideCompany,
}: {
  order: TopupOrderListItem;
  hideCompany: boolean;
}) {
  return (
    <tr className="border-b hover:bg-muted/30 transition-colors">
      <td className="p-4 text-sm">
        <Link to={`/orders/${order.id}`} className="text-primary hover:underline">
          {formatDate(order.createdAt)}
        </Link>
      </td>
      <td className="p-4 text-sm">
        <div>
          <p className="font-medium">{customerLabel(order)}</p>
          <p className="text-xs text-muted-foreground">{order.user?.email || ''}</p>
        </div>
      </td>
      {!hideCompany && (
        <td className="p-4 text-sm text-muted-foreground">{order.company?.name || '—'}</td>
      )}
      <td className="p-4 text-sm text-right font-medium">
        {formatAmount(order.amount)} {order.currency}
      </td>
      <td className="p-4">
        <PaymentMethodBadge order={order} />
      </td>
      <td className="p-4 text-center">
        <TopupStatusBadge status={order.status} />
      </td>
      <td className="p-4 text-center">
        <FiscalIndicator fiscal={order.fiscalReceipt} />
      </td>
    </tr>
  );
}

const topupStatusConfig: Record<
  TopupOrderStatus,
  { label: string; variant: 'success' | 'danger' | 'warning' | 'secondary' | 'info' }
> = {
  pending: { label: 'Pending', variant: 'secondary' },
  processing_payment: { label: 'Processing', variant: 'warning' },
  payment_succeeded: { label: 'Paid', variant: 'info' },
  fiscalizing: { label: 'Fiscalizing', variant: 'info' },
  completed: { label: 'Completed', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
  cancelled: { label: 'Cancelled', variant: 'secondary' },
  refunded: { label: 'Refunded', variant: 'secondary' },
};

export function TopupStatusBadge({ status }: { status: TopupOrderStatus }) {
  const cfg = topupStatusConfig[status] || { label: status, variant: 'secondary' as const };
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
}

export function PaymentMethodBadge({
  order,
}: {
  order: Pick<TopupOrderListItem, 'paymentMethod' | 'savedCard'>;
}) {
  const label = order.paymentMethod === 'mit'
    ? order.savedCard
      ? `${order.savedCard.cardBrand || 'Card'} •${(order.savedCard.maskedPan || '').slice(-4)}`
      : 'Saved card'
    : 'New card';
  return (
    <div className="inline-flex items-center gap-1 text-xs">
      <CreditCard className="w-3.5 h-3.5 text-muted-foreground" />
      <span>{label}</span>
    </div>
  );
}

export function FiscalIndicator({
  fiscal,
  compact,
}: {
  fiscal?: {
    status: FiscalReceiptStatus;
    verificationUrl: string | null;
    pdfUrl: string | null;
    invoiceNumber: string | null;
  } | null;
  compact?: boolean;
}) {
  if (!fiscal) return <span className="text-xs text-muted-foreground">—</span>;
  if (fiscal.status === 'fiscalized') {
    return (
      <div className="flex items-center gap-1.5 justify-center">
        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        {!compact && fiscal.verificationUrl && (
          <a
            href={fiscal.verificationUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-primary hover:underline"
          >
            Verify
          </a>
        )}
        {!compact && fiscal.pdfUrl && (
          <a
            href={fiscal.pdfUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-emerald-600 hover:underline inline-flex items-center gap-0.5"
          >
            <FileText className="w-3 h-3" />
            PDF
          </a>
        )}
      </div>
    );
  }
  if (fiscal.status === 'failed') {
    return (
      <div className="flex items-center gap-1.5 text-xs text-destructive justify-center">
        <XCircle className="w-4 h-4" />
        <span>Error</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground justify-center">
      <Clock className="w-4 h-4" />
      <span>In progress</span>
    </div>
  );
}

function customerLabel(order: TopupOrderListItem): string {
  const u = order.user;
  if (!u) return '—';
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return name || u.email || '—';
}
